import { useCallback, useEffect, useRef, useState } from 'react'

import { getVcnBulkJobCurrent } from '../../../api/vcn-management-api-client'
import { VcnBulkJobState } from '../../../api/dto/vcn-bulk-job.dto'
import type { VcnBulkJobStatus } from '../../../api/dto/vcn-bulk-job.dto'

const ACTIVE_STATES = new Set([VcnBulkJobState.QUEUED, VcnBulkJobState.RUNNING])
const POLLING_STATES = new Set([
  VcnBulkJobState.QUEUED,
  VcnBulkJobState.RUNNING,
  VcnBulkJobState.TIMEOUT,
])

export function useVcnBulkJobCurrent() {
  const [job, setJob] = useState<VcnBulkJobStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const refresh = useCallback(async () => {
    try {
      setError(null)
      const next = await getVcnBulkJobCurrent()
      if (!mountedRef.current) return
      setJob(next)
    } catch (e) {
      if (!mountedRef.current) return
      setError(e instanceof Error ? e.message : 'Failed to load job status')
    } finally {
      if (mountedRef.current) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [])

  useEffect(() => {
    if (!job || !POLLING_STATES.has(job.state)) return
    const interval = job.state === VcnBulkJobState.TIMEOUT ? 30_000 : 1_500
    const id = window.setInterval(() => {
      void refresh()
    }, interval)
    return () => window.clearInterval(id)
  }, [job?.jobId, job?.state, refresh])

  const isJobActive = Boolean(job && ACTIVE_STATES.has(job.state))

  return { job, loading, error, refresh, isJobActive }
}
